import React, { useCallback, useEffect, useState } from 'react';

import { setLastLoginMethod } from 'js/src/components/auth/common/lastLoginMethod';
import { useAuthReasonToast } from 'js/src/components/auth/common/useAuthReasonToast';
import { PasswordWithCriteria } from 'js/src/components/auth/common/Password';
import SocialButton, { SOCIAL_BUTTON_PROVIDERS } from 'js/src/components/auth/common/SocialButton';
import { useFormFeedback } from 'js/src/components/auth/common/useFormFeedback';
import type { FormFeedbackAction } from 'js/src/components/auth/common/useFormFeedback';
import Button, { BUTTON_TYPE, BUTTON_VARIANTS } from 'js/src/components/common/ui/Button';
import Callout, { CALLOUT_TYPES } from 'js/src/components/common/ui/Callout';
import TextInput, {
    AUTOCOMPLETE_VALUES,
    TEXT_INPUT_VARIANTS,
    type TypeInputChangeEvent,
} from 'js/src/components/common/ui/TextInput';
import { loginSignupOperation } from 'js/src/data/account';
import { buildSocialAuthUrl } from 'js/src/data/account/utils';
import { cleanEmail, isEmailValid } from 'js/src/libs/custom-utils';
import PasswordValidation from 'js/src/libs/password-validation';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';

const isNameTooLong = (nameVal: string) => {
    const split_names = nameVal.split(' ');
    let first_name = nameVal;
    let last_name = '';
    if (split_names.length >= 2) {
        first_name = split_names.shift() || '';
        last_name = split_names.join(' ');
    }

    if (first_name.length > 30) {
        return true;
    }
    if (last_name.length > 30) {
        return true;
    }

    return false;
};

const onSubmitValidation = ({
    emailVal,
    passwordVal,
    nameVal,
    setFormFeedback,
}: {
    emailVal: string;
    passwordVal: string;
    nameVal: string;
    setFormFeedback: React.Dispatch<FormFeedbackAction>;
}) => {
    let formValid = true;

    if (nameVal === '') {
        setFormFeedback({
            type: 'SET_ERROR',
            payload: {
                name: 'Please provide your full name.',
            },
        });

        formValid = false;
    }

    if (isNameTooLong(nameVal)) {
        setFormFeedback({
            type: 'SET_ERROR',
            payload: {
                name: 'Your name is too long, please use a shorter one.',
            },
        });
        formValid = false;
    }

    if (!isEmailValid(emailVal)) {
        setFormFeedback({
            type: 'SET_ERROR',
            payload: {
                email: 'Please provide a valid email address.',
            },
        });

        formValid = false;
    }

    if (passwordVal === '') {
        setFormFeedback({
            type: 'SET_ERROR',
            payload: {
                password: 'Please provide a password.',
            },
        });

        formValid = false;
    }

    // password criteria validation
    if (
        !PasswordValidation.lowercaseCondition(passwordVal) ||
        !PasswordValidation.uppercaseCondition(passwordVal) ||
        !PasswordValidation.characterMinimumCondition(passwordVal) ||
        !PasswordValidation.numberSymbolCondition(passwordVal)
    ) {
        setFormFeedback({
            type: 'SET_ERROR',
            payload: {
                password: 'Your password must meet the criteria.',
            },
        });

        formValid = false;
    }

    return formValid;
};

type Props = {
    email?: string;
    facebookAuthUrl: string;
    googleAuthUrl: string;
    loginUrl: string;
    currentPath?: string;
};

const SignUpForm = React.memo(function SignUpForm({
    email = '',
    facebookAuthUrl,
    googleAuthUrl,
    loginUrl,
    currentPath = '',
}: Props) {
    const [signingUp, setSigningUp] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [nameVal, setNameVal] = useState('');
    const [emailVal, setEmail] = useState(email);
    const [passwordVal, setPasswordVal] = useState('');
    const [formFeedback, setFormFeedback] = useFormFeedback({
        global: '',
        email: '',
        password: '',
        name: '',
    });

    const positionOfQueryParams = currentPath.indexOf('?');
    const hasQueryParamsInCurrentPath = positionOfQueryParams > -1;
    let sourceRedirectUrl = `${loginUrl}?is_signup=1`; // add flag to tell auth pipeline to create user
    if (hasQueryParamsInCurrentPath) {
        const extraParams = currentPath.substring(positionOfQueryParams + 1);
        sourceRedirectUrl += `&${extraParams}`;
    }

    useEffect(() => {
        if (formFeedback.global !== '') {
            setShowEmailForm(true);
        }
    }, [formFeedback]);

    useAuthReasonToast();

    const handleShowEmailForm = useCallback(() => {
        setShowEmailForm(true);
    }, []);

    const facebookOnclick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setLastLoginMethod(SOCIAL_BUTTON_PROVIDERS.FACEBOOK);
            window.location.href = buildSocialAuthUrl(facebookAuthUrl, sourceRedirectUrl);
        },
        [facebookAuthUrl, sourceRedirectUrl],
    );

    const googleOnclick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setLastLoginMethod(SOCIAL_BUTTON_PROVIDERS.GOOGLE);
            window.location.href = buildSocialAuthUrl(googleAuthUrl, sourceRedirectUrl);
        },
        [googleAuthUrl, sourceRedirectUrl],
    );

    const passwordOnChangeHandler = useCallback(
        (e: TypeInputChangeEvent) => setPasswordVal(e.target.value),
        [],
    );

    const emailOnChangeHandler = useCallback(
        (e: TypeInputChangeEvent) => setEmail(e.currentTarget.value),
        [],
    );

    const onNameChange = useCallback(
        (e: TypeInputChangeEvent) => setNameVal(e.currentTarget.value),
        [],
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // don't allow duplicate requests as this will create duplicate accounts with the same email
        if (signingUp) return;

        setFormFeedback({ type: 'CLEAR_ERRORS' });

        if (
            !onSubmitValidation({
                nameVal,
                emailVal,
                passwordVal,
                setFormFeedback,
            })
        ) {
            return;
        }

        setSigningUp(true);
        setLastLoginMethod('email');
        const lowerCaseEmail = cleanEmail(emailVal);

        loginSignupOperation({
            name: nameVal,
            email: lowerCaseEmail,
            password: passwordVal,
            is_signup: '1',
        })
            .done((response) => {
                const {
                    next = '',
                    invited = false,
                    mfa_url = '',
                    confirmation_url = '',
                } = response;
                if (mfa_url) {
                    window.location.href = mfa_url;
                } else if (confirmation_url) {
                    window.location.href = confirmation_url;
                } else {
                    analytics.trackSignUp(() => {
                        window.location.href =
                            invited === '1'
                                ? QueryStringManipulator.updateQueryStringParameter(
                                      next,
                                      'invited',
                                      invited,
                                  )
                                : next;
                    });
                }
            })
            .fail((xhr) => {
                if (xhr.getResponseHeader('cf-mitigated') === 'challenge') {
                    // Cloudflare's Web Application Firewall is challenging that this
                    // is a valid person (and not a bot). We need to reload the page
                    // to show the user the challenge page. If they pass that challenge,
                    // the next time they submit the form, it should go through.
                    window.location.reload();
                    return;
                }
                const { responseJSON = {} } = xhr;
                setFormFeedback({
                    type: 'SET_ERROR',
                    payload: responseJSON,
                });
            })
            .always(() => setSigningUp(false));
    };
    const queryString = window.location.search || '';

    return (
        <div>
            <div className="mb-l5-16">
                <SocialButton
                    provider={SOCIAL_BUTTON_PROVIDERS.GOOGLE}
                    onClick={googleOnclick}
                    text="Sign up with Google"
                    data-testid="google-signup"
                />
            </div>
            <div className="mb-l5-16">
                <SocialButton
                    provider={SOCIAL_BUTTON_PROVIDERS.FACEBOOK}
                    onClick={facebookOnclick}
                    text="Sign up with Facebook"
                    data-testid="facebook-login"
                />
            </div>
            <div className="divider text-muted my-l5-32">
                <hr />
                <span>or</span>
                <hr />
            </div>
            {!showEmailForm ? (
                <div>
                    <div className="mb-l5-24">
                        <Button
                            variant={BUTTON_VARIANTS.OUTLINE}
                            className="d-block w-100"
                            onClick={handleShowEmailForm}
                            text="Sign up with email"
                            data-testid="email-signup-btn"
                        />
                    </div>
                </div>
            ) : (
                <div>
                    {
                        // global form errors
                        formFeedback.global !== '' && (
                            <Callout
                                msgType={CALLOUT_TYPES.ERROR}
                                text={
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: formFeedback.global,
                                        }}
                                    />
                                }
                            />
                        )
                    }

                    {/*
                       To get password managers to work nicely,
                       we create 2 <form/> tags:
                           - one for just the "Full Name"
                           - a second for username + password
                    */}
                    <form
                        id="name-signup-form"
                        method="post"
                        className="form-sidebar"
                        data-testid="name-signup-form"
                        onSubmit={handleSubmit}
                    >
                        <div className="mb-l5-16" data-testid="name-container">
                            <TextInput
                                variant={TEXT_INPUT_VARIANTS.STANDARD}
                                type="text"
                                id="name"
                                data-testid="name"
                                text={nameVal}
                                onChange={onNameChange}
                                labelText="Full name"
                                formFeedbackMessage={formFeedback.name}
                                autocomplete={AUTOCOMPLETE_VALUES.NAME}
                                ignorePasswordManagers={false}
                            />
                        </div>
                    </form>
                    <form
                        id="signup-form"
                        method="post"
                        className="form-sidebar"
                        data-testid="signup-form"
                        onSubmit={handleSubmit}
                    >
                        <div className="mb-l5-16" data-testid="email-container">
                            <TextInput
                                text={emailVal}
                                labelText="Work email"
                                variant={TEXT_INPUT_VARIANTS.STANDARD}
                                onChange={emailOnChangeHandler}
                                formFeedbackMessage={formFeedback.email}
                                data-testid="email"
                                autocomplete={AUTOCOMPLETE_VALUES.EMAIL}
                                ignorePasswordManagers={false}
                            />
                        </div>

                        <div className="password-container mb-l5-24">
                            <PasswordWithCriteria
                                passwordVal={passwordVal}
                                onChange={passwordOnChangeHandler}
                                formFeedbackMessage={formFeedback.password}
                            />
                        </div>

                        <div className="mb-l5-16">
                            <Button
                                data-testid="signup-btn"
                                type={BUTTON_TYPE.SUBMIT}
                                className="d-block w-100"
                                disabled={signingUp}
                                text={signingUp ? 'Creating account...' : 'Create account'}
                            />
                        </div>
                    </form>
                </div>
            )}
            <div className="text-center mt-l5-32">
                <span>Already have an account? </span>
                <a href={loginUrl + queryString} className="fw-semibold text-decoration-none">
                    Log in
                </a>
            </div>
        </div>
    );
});

export default SignUpForm;
